import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Button, Flex, Grid, HStack, Input, InputGroup, InputLeftElement, Select, Table, Tbody, Td, Text, Th, Thead, Tr, VisuallyHidden,
} from "@chakra-ui/react";
import { ArrowDown, ArrowUp, Search, X } from "lucide-react";
import { getMetricasAnunciosLista } from "../../services/api";
import { CarregandoSobre, ErroCarga, Paginacao, Vazio, diaBR, num, useCelular } from "../SaudeAnunciosML/comum";
import { BadgeTipo, CONTAS, SeletorDias, TAMANHO_CAMPO, brl, conversao } from "./base";
import CalculoPreco from "./CalculoPreco";

const FILTROS_PADRAO = { busca: "", conta: "", tipo: "", status: "", situacao: "", faixa: "" };

const FAIXAS = [
  { valor: "", label: "Preço" },
  { valor: "0-79", label: "Até R$ 79" },
  { valor: "79-150", label: "R$ 79-150" },
  { valor: "150-300", label: "R$ 150-300" },
  { valor: "300-1000", label: "R$ 300-1 mil" },
  { valor: "1000-", label: "> R$ 1 mil" },
];

const COLUNAS = [
  { chave: "visitas", label: "Visitas", valor: (i) => num(i.visitas) },
  { chave: "unidades", label: "Vendas", valor: (i) => num(i.unidades) },
  { chave: "conversao", label: "Conversão", valor: (i) => conversao(i.pedidos, i.visitas) },
  { chave: "receita", label: "Receita", valor: (i) => brl(i.receita, 0) },
  { chave: "lucro", label: "Lucro", valor: (i) => brl(i.lucro, 0) },
  { chave: "preco", label: "Preço", valor: (i) => brl(i.preco) },
];

function Ordenavel({ coluna, ordem, direcao, onOrdenar }) {
  const ativa = ordem === coluna.chave;
  return (
    <Th isNumeric p={0}>
      <Button variant="ghost" size="xs" w="100%" justifyContent="flex-end" fontSize="xs" textTransform="uppercase" letterSpacing="wide"
        onClick={() => onOrdenar(coluna.chave)} aria-sort={ativa ? (direcao === "asc" ? "ascending" : "descending") : "none"}
        rightIcon={ativa ? (direcao === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : undefined}>
        {coluna.label}
      </Button>
    </Th>
  );
}

function CartaoAnuncio({ i, onAbrir }) {
  return (
    <Box position="relative">
      <Box p={3} pr={12} as="button" type="button" w="100%" textAlign="left" _hover={{ bg: "gray.50" }} _focusVisible={{ boxShadow: "outline", outline: "none" }}
        onClick={() => onAbrir(i.id_ml)}>
        <Text fontSize="sm" color="gray.800" noOfLines={2}>{i.nome || "Produto sem nome"}</Text>
        <HStack spacing={1} mt={1} flexWrap="wrap">
          <Text fontSize="xs" color="gray.600">{i.id_ml} · Conta {i.conta_id} · SKU {i.produto_id ?? "—"}</Text>
          <BadgeTipo tipo={i.listing_type_id} />
          {i.status === "paused" && <Text fontSize="xs" color="orange.600">pausado</Text>}
        </HStack>
        <Grid templateColumns="repeat(3, minmax(0, 1fr))" gap={2} mt={2} fontSize="xs" sx={{ fontVariantNumeric: "tabular-nums" }}>
          {COLUNAS.map((c) => (
            <Box key={c.chave} minW={0}>
              <Text color="gray.600">{c.label}</Text>
              <Text fontWeight="semibold" color="gray.800">{c.valor(i)}</Text>
            </Box>
          ))}
        </Grid>
      </Box>
      <Box position="absolute" top={2} right={2}>
        <CalculoPreco idMl={i.id_ml} />
      </Box>
    </Box>
  );
}

export default function AnunciosTab({ onAbrir }) {
  const celular = useCelular();
  const [periodo, setPeriodo] = useState(30);
  const [filtros, setFiltros] = useState(FILTROS_PADRAO);
  const [busca, setBusca] = useState("");
  const [ordem, setOrdem] = useState("visitas");
  const [direcao, setDirecao] = useState("desc");
  const [pagina, setPagina] = useState(1);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(false);
  const ultima = useRef(0);
  const topo = useRef(null);

  useEffect(() => {
    const id = setTimeout(() => {
      setFiltros((f) => (f.busca === busca.trim() ? f : { ...f, busca: busca.trim() }));
      setPagina(1);
    }, 400);
    return () => clearTimeout(id);
  }, [busca]);

  const carregar = useCallback(async () => {
    const minha = ++ultima.current;
    setCarregando(true);
    const [min, max] = filtros.faixa ? filtros.faixa.split("-") : ["", ""];
    const r = await getMetricasAnunciosLista({
      periodo, pagina, ordem, direcao,
      busca: filtros.busca || undefined,
      conta: filtros.conta || undefined,
      tipo: filtros.tipo || undefined,
      status: filtros.status || undefined,
      situacao: filtros.situacao || undefined,
      preco_min: min || undefined,
      preco_max: max || undefined,
    });
    if (minha !== ultima.current) return;
    setCarregando(false);
    setErro(!r);
    if (r) setDados(r);
  }, [periodo, pagina, ordem, direcao, filtros]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => () => { ultima.current += 1; }, []);

  const mudar = (campo) => (e) => {
    setFiltros((f) => ({ ...f, [campo]: e.target.value }));
    setPagina(1);
  };

  const ordenar = (chave) => {
    if (chave === ordem) setDirecao((d) => (d === "desc" ? "asc" : "desc"));
    else {
      setOrdem(chave);
      setDirecao("desc");
    }
    setPagina(1);
  };

  const limpar = () => {
    setBusca("");
    setFiltros(FILTROS_PADRAO);
    setPagina(1);
  };

  const temFiltro = busca.trim() !== "" || Object.keys(FILTROS_PADRAO).some((k) => k !== "busca" && filtros[k] !== "");

  return (
    <Box p={{ base: 3, md: 5 }} ref={topo}>
      <Flex gap={2} mb={3} flexWrap="wrap" align="center">
        <SeletorDias valor={periodo} onChange={(d) => { setPeriodo(d); setPagina(1); }} />
        <InputGroup size={TAMANHO_CAMPO} w={{ base: "100%", md: "320px" }}>
          <InputLeftElement pointerEvents="none"><Search size={14} color="#718096" /></InputLeftElement>
          <Input bg="white" placeholder="MLB, SKU, código ou nome" value={busca} onChange={(e) => setBusca(e.target.value)} aria-label="Buscar anúncio" />
        </InputGroup>
      </Flex>
      <Grid templateColumns={{ base: "1fr 1fr", md: "repeat(6, minmax(0, 1fr))" }} gap={2} mb={3}>
        <Select size={TAMANHO_CAMPO} bg="white" value={filtros.conta} onChange={mudar("conta")} aria-label="Conta">
          <option value="">Contas</option>
          {CONTAS.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </Select>
        <Select size={TAMANHO_CAMPO} bg="white" value={filtros.tipo} onChange={mudar("tipo")} aria-label="Tipo">
          <option value="">Tipos</option>
          <option value="gold_pro">Premium</option>
          <option value="gold_special">Clássico</option>
        </Select>
        <Select size={TAMANHO_CAMPO} bg="white" value={filtros.status} onChange={mudar("status")} aria-label="Status">
          <option value="">Status</option>
          <option value="active">Ativos</option>
          <option value="paused">Pausados</option>
        </Select>
        <Select size={TAMANHO_CAMPO} bg="white" value={filtros.situacao} onChange={mudar("situacao")} aria-label="Situação">
          <option value="">Movimento</option>
          <option value="com_venda">Com venda</option>
          <option value="sem_venda">Sem venda</option>
          <option value="sem_visita">Sem visita</option>
        </Select>
        <Select size={TAMANHO_CAMPO} bg="white" value={filtros.faixa} onChange={mudar("faixa")} aria-label="Faixa de preço">
          {FAIXAS.map((f) => <option key={f.valor} value={f.valor}>{f.label}</option>)}
        </Select>
        {celular ? (
          <Select size={TAMANHO_CAMPO} bg="white" value={`${ordem}:${direcao}`} aria-label="Ordenar"
            onChange={(e) => { const [o, d] = e.target.value.split(":"); setOrdem(o); setDirecao(d); setPagina(1); }}>
            {COLUNAS.flatMap((c) => [
              <option key={`${c.chave}:desc`} value={`${c.chave}:desc`}>{c.label} ↓</option>,
              <option key={`${c.chave}:asc`} value={`${c.chave}:asc`}>{c.label} ↑</option>,
            ])}
          </Select>
        ) : (
          temFiltro ? <Button size={TAMANHO_CAMPO} variant="ghost" leftIcon={<X size={14} />} onClick={limpar}>Limpar filtros</Button> : <Box />
        )}
      </Grid>
      {celular && temFiltro && (
        <Button size={TAMANHO_CAMPO} variant="ghost" leftIcon={<X size={14} />} onClick={limpar} mb={2}>Limpar filtros</Button>
      )}

      {erro && !dados && <ErroCarga onTentar={carregar} carregando={carregando} />}

      {dados && (
        <>
          <Text fontSize="xs" color="gray.600" mb={2}>{num(dados.total)} anúncio(s)</Text>
          <Box position="relative" bg="white" borderWidth="1px" borderColor="gray.100" borderRadius="lg" minH="120px">
            {carregando && <CarregandoSobre />}
            {!dados.itens.length ? (
              <Vazio titulo="Nenhum anúncio com esses filtros." acao={temFiltro ? <Button size="sm" onClick={limpar}>Limpar filtros</Button> : null} />
            ) : celular ? (
              <Box sx={{ "& > *:not(:last-child)": { borderBottomWidth: "1px", borderColor: "gray.100" } }}>
                {dados.itens.map((i) => <CartaoAnuncio key={i.id_ml} i={i} onAbrir={onAbrir} />)}
              </Box>
            ) : (
              <Box overflowX="auto">
                <Table size="sm" sx={{ fontVariantNumeric: "tabular-nums" }}>
                  <Thead>
                    <Tr>
                      <Th>Anúncio</Th>
                      <Th>Tipo</Th>
                      {COLUNAS.map((c) => <Ordenavel key={c.chave} coluna={c} ordem={ordem} direcao={direcao} onOrdenar={ordenar} />)}
                      <Th>Última venda</Th>
                      <Th w="1%"><VisuallyHidden>Cálculo do preço</VisuallyHidden></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {dados.itens.map((i) => (
                      <Tr key={i.id_ml} cursor="pointer" _hover={{ bg: "gray.50" }} onClick={() => onAbrir(i.id_ml)}
                        tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") onAbrir(i.id_ml); }}>
                        <Td maxW="380px">
                          <Text fontSize="sm" color="gray.800" noOfLines={1} title={i.nome || ""}>{i.nome || "Produto sem nome"}</Text>
                          <Text fontSize="xs" color="gray.600">
                            {i.id_ml} · Conta {i.conta_id} · SKU {i.produto_id ?? "—"}{i.status === "paused" ? " · pausado" : ""}
                          </Text>
                        </Td>
                        <Td><BadgeTipo tipo={i.listing_type_id} /></Td>
                        {COLUNAS.map((c) => <Td key={c.chave} isNumeric>{c.valor(i)}</Td>)}
                        <Td>{i.ultima_venda ? diaBR(i.ultima_venda) : "—"}</Td>
                        <Td px={1}><CalculoPreco idMl={i.id_ml} /></Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </Box>
          <Paginacao pagina={dados.pagina} porPagina={dados.por_pagina} total={dados.total} carregando={carregando} onPagina={setPagina} rolarPara={topo} />
        </>
      )}
      {!dados && !erro && <Box position="relative" minH="160px"><CarregandoSobre /></Box>}
    </Box>
  );
}
